import { Box, MenuItem } from "@mui/material";
import { FC, useState } from "react";
import { Button, PaperHeader, Price, Select, Table } from "ui";
import { IFormChangeResponse } from "ui/Form/Form";
import { IFormContext } from "ui/Form/FormContext";

type TGastroPayCommissions = {
    context: IFormContext;
    errors: string[];
    commissionsId: string;
};

const GastroPayCommissions: FC<TGastroPayCommissions> = ({
    context,
    errors,
    commissionsId,
}) => {
    console.log("GastroPayCommissions context.data", context.data);
    const [bulkCommissions, setBulkCommissions] = useState({
        low_fee: 100,
        low_percentage: 165,
        high_fee: 200,
        high_percentage: 495,
    });

    const handleBulkChange = (field: string, value: number) => {
        setBulkCommissions((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const updateCommissions = () => {
        const updatedCommissions = context.data[commissionsId].map(
            (commission: any) => {
                if (commission.product_platform === "consumer") {
                    return {
                        ...commission,
                        domestic_fixed: bulkCommissions.low_fee,
                        domestic_percentage: bulkCommissions.low_percentage,
                        eea_percentage: bulkCommissions.low_percentage,
                        eea_fixed: bulkCommissions.low_fee,
                        eea_uk_percentage: bulkCommissions.high_percentage,
                        eea_uk_fixed: bulkCommissions.high_fee,
                        europe_percentage: bulkCommissions.high_percentage,
                        europe_fixed: bulkCommissions.high_fee,
                        inter_percentage: bulkCommissions.high_percentage,
                        inter_fixed: bulkCommissions.high_fee,
                    };
                } else if (commission.product_platform === "commercial") {
                    return {
                        ...commission,
                        domestic_fixed: bulkCommissions.high_fee,
                        domestic_percentage: bulkCommissions.high_percentage,
                        eea_percentage: bulkCommissions.high_percentage,
                        eea_fixed: bulkCommissions.high_fee,
                        eea_uk_percentage: bulkCommissions.high_percentage,
                        eea_uk_fixed: bulkCommissions.high_fee,
                        europe_percentage: bulkCommissions.high_percentage,
                        europe_fixed: bulkCommissions.high_fee,
                        inter_percentage: bulkCommissions.high_percentage,
                        inter_fixed: bulkCommissions.high_fee,
                    };
                }
                return commission;
            },
        );

        context.onChange({
            id: commissionsId,
            value: updatedCommissions,
        });
    };

    return (
        <Box sx={{ mt: 2 }}>
            <PaperHeader sx={{ mt: 2 }} title="Commissions" />
            {errors && errors.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    {errors.map((error, index) => (
                        <Box key={index} color="error.main">
                            {error}
                        </Box>
                    ))}
                </Box>
            )}

            <Box
                sx={{
                    mb: 2,
                    mt: 2,
                    flex: "row",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                }}
            >
                <Box sx={{ backgroundColor: "#88E788", flex: 1, p: 1, mr: 1 }}>
                    <Price
                        id={`low_percentage`}
                        value={bulkCommissions.low_percentage}
                        onChange={(a: IFormChangeResponse) =>
                            handleBulkChange("low_percentage", a.value)
                        }
                        label="Low Percentage"
                        fullWidth
                    />
                </Box>
                <Box sx={{ backgroundColor: "#88E788", flex: 1, p: 1, mr: 1 }}>
                    <Price
                        id={`low_fee`}
                        value={bulkCommissions.low_fee}
                        onChange={(a: IFormChangeResponse) =>
                            handleBulkChange("low_fee", a.value)
                        }
                        label="Low Fee"
                        fullWidth
                    />
                </Box>

                <Box sx={{ backgroundColor: "#FFB3B3", flex: 1, p: 1, mr: 1 }}>
                    <Price
                        id={`high_percentage`}
                        value={bulkCommissions.high_percentage}
                        onChange={(a: IFormChangeResponse) =>
                            handleBulkChange("high_percentage", a.value)
                        }
                        label="High Percentage"
                        fullWidth
                    />
                </Box>
                <Box sx={{ backgroundColor: "#FFB3B3", flex: 1, p: 1, mr: 1 }}>
                    <Price
                        id={`high_fee`}
                        value={bulkCommissions.high_fee}
                        onChange={(a: IFormChangeResponse) =>
                            handleBulkChange("high_fee", a.value)
                        }
                        label="High Fee"
                        fullWidth
                    />
                </Box>
                <Button onClick={updateCommissions}>Set</Button>
            </Box>

            <Table
                id="gastro-pay-commissions-table"
                columns={[
                    {
                        field: "product_platform",
                        headName: "Platform",
                        renderCell: (item, key, idx) => ({
                            children: (
                                <Select
                                    id={`${commissionsId}.${idx}.product_platform`}
                                    sx={{ width: "150px" }}
                                    disabled={idx < 3} // Disable first 3 rows
                                >
                                    <MenuItem value="consumer">
                                        Consumer
                                    </MenuItem>
                                    <MenuItem value="commercial">
                                        Commercial
                                    </MenuItem>
                                </Select>
                            ),
                        }),
                    },
                    {
                        field: "payment_product_type",
                        headName: "Product Type",
                        renderCell: (item, key, idx) => {
                            if (item.payment_product_type === null) {
                                return {
                                    children: "All",
                                };
                            }

                            return {
                                children: (
                                    <Select
                                        id={`${commissionsId}.${idx}.payment_product_type`}
                                        sx={{ width: "150px" }}
                                        disabled={idx < 3} // Disable first 3 rows
                                    >
                                        <MenuItem value="bambora.applepay">
                                            bambora.applepay
                                        </MenuItem>
                                        <MenuItem value="bambora.creditcard">
                                            bambora.creditcard
                                        </MenuItem>
                                        <MenuItem value="bambora.googlepay">
                                            bambora.googlepay
                                        </MenuItem>
                                        <MenuItem value="bambora.vipps">
                                            bambora.vipps
                                        </MenuItem>
                                        <MenuItem value="klarna.klarna">
                                            klarna.klarna
                                        </MenuItem>
                                    </Select>
                                ),
                            };
                        },
                    },
                    {
                        field: "domestic_percentage",
                        headName: "Domestic %",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.domestic_percentage`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: {
                                    backgroundColor:
                                        item.product_platform === "consumer"
                                            ? "#88E788"
                                            : "#FFB3B3",
                                },
                            };
                        },
                    },
                    {
                        field: "domestic_fixed",
                        headName: "Domestic Fixed",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.domestic_fixed`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: {
                                    backgroundColor:
                                        item.product_platform === "consumer"
                                            ? "#88E788"
                                            : "#FFB3B3",
                                },
                            };
                        },
                    },
                    {
                        field: "eea_percentage",
                        headName: "EEA %",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.eea_percentage`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: {
                                    backgroundColor:
                                        item.product_platform === "consumer"
                                            ? "#88E788"
                                            : "#FFB3B3",
                                    borderLeft: "1px solid #ccc",
                                },
                            };
                        },
                    },
                    {
                        field: "eea_fixed",
                        headName: "EEA Fixed",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.eea_fixed`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: {
                                    backgroundColor:
                                        item.product_platform === "consumer"
                                            ? "#88E788"
                                            : "#FFB3B3",
                                },
                            };
                        },
                    },
                    {
                        field: "eea_uk_percentage",
                        headName: "EEA UK %",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.eea_uk_percentage`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: {
                                    backgroundColor: "#FFB3B3",
                                    borderLeft: "1px solid #ccc",
                                },
                            };
                        },
                    },
                    {
                        field: "eea_uk_fixed",
                        headName: "EEA UK Fixed",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.eea_uk_fixed`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: { backgroundColor: "#FFB3B3" },
                            };
                        },
                    },
                    {
                        field: "europe_percentage",
                        headName: "Europe %",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.europe_percentage`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: {
                                    backgroundColor: "#FFB3B3",
                                    borderLeft: "1px solid #ccc",
                                },
                            };
                        },
                    },
                    {
                        field: "europe_fixed",
                        headName: "Europe Fixed",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.europe_fixed`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: { backgroundColor: "#FFB3B3" },
                            };
                        },
                    },
                    {
                        field: "inter_percentage",
                        headName: "Inter %",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.inter_percentage`}
                                        sx={{ width: "75px" }}
                                    />
                                ),
                                sx: {
                                    backgroundColor: "#FFB3B3",
                                    borderLeft: "1px solid #ccc",
                                },
                            };
                        },
                    },
                    {
                        field: "inter_fixed",
                        headName: "Inter Fixed",
                        renderCell(item, key, idx) {
                            return {
                                children: (
                                    <Price
                                        id={`${commissionsId}.${idx}.inter_fixed`}
                                        sx={{ width: "100px" }}
                                    />
                                ),
                                sx: {
                                    backgroundColor: "#FFB3B3",
                                },
                            };
                        },
                    },
                ]}
                rows={context.data[commissionsId] || []}
            />
        </Box>
    );
};

export default GastroPayCommissions;
